import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Clock, CreditCard, Car, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";

const TermsPage = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-saturn-black text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-saturn-black/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/zdb6hh5n_Eima%20logo%20Speed%20rate%20font%2020x20.png"
              alt="Saturn Styling"
              className="w-10 h-10 object-contain"
            />
            <span className="font-orbitron font-bold text-lg text-white">SATURNSTYLING</span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-2">
            Terms & <span className="text-saturn-orange">Conditions</span>
          </h1>
          <p className="text-slate-400 mb-8">Last updated: March 2026</p>

          {/* Quick Summary */}
          <div className="glass rounded-xl p-6 mb-8 border-saturn-orange/30">
            <h2 className="font-rajdhani font-bold text-lg text-saturn-orange mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Quick Summary
            </h2>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <span>€50 non-refundable deposit required to secure your booking</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <span>Warranties range from 1-5 years depending on product selected</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <span>24-hour cancellation notice required for deposit transfer</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <span>All job durations are approximate estimates only</span>
              </li>
            </ul>
          </div>

          {/* Section 1: Deposits */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-saturn-orange/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-saturn-orange" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">1. Deposits & Payment</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Booking Deposit</h3>
                <p className="text-slate-400 text-sm">
                  A non-refundable deposit of <span className="text-saturn-orange font-bold">€50</span> is required to confirm and secure your appointment. 
                  This deposit will be deducted from your final bill upon completion of the work.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Payment Methods</h3>
                <p className="text-slate-400 text-sm">
                  We accept cash, debit cards, credit cards, and bank transfer. Deposits are processed securely through Revolut Business. 
                  Full payment is due upon collection of your vehicle.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Deposit Non-Refundability</h3>
                <p className="text-slate-400 text-sm">
                  The €50 deposit is non-refundable as it covers administrative costs and secures your time slot, 
                  preventing other customers from booking. This policy is standard practice in the automotive styling industry.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Cancellation */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">2. Cancellation & Rescheduling</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Cancellation Policy</h3>
                <p className="text-slate-400 text-sm">
                  If you need to cancel your appointment, please provide at least <span className="text-saturn-orange font-bold">24 hours notice</span>. 
                  With adequate notice, your deposit can be transferred to a future booking within 30 days.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">No-Shows</h3>
                <p className="text-slate-400 text-sm">
                  Failure to attend your appointment without prior notice will result in forfeiture of your deposit. 
                  No refunds or transfers will be provided for no-shows.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Rescheduling</h3>
                <p className="text-slate-400 text-sm">
                  Appointments can be rescheduled free of charge with at least 24 hours notice, subject to availability. 
                  Your deposit will be automatically transferred to your new appointment date.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Warranties */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">3. Warranty Information</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Window Tint Warranties</h3>
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <p className="text-saturn-orange font-rajdhani font-bold">Performance</p>
                    <p className="text-slate-400 text-sm">1 Year warranty</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <p className="text-green-400 font-rajdhani font-bold">Savvy Ceramic</p>
                    <p className="text-slate-400 text-sm">5 Year warranty</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">What's Covered</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Bubbling, peeling, or cracking of window film under normal conditions</li>
                  <li>• Discoloration or fading of tint material</li>
                  <li>• Delamination of film from glass</li>
                  <li>• Manufacturing defects in materials</li>
                </ul>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">What's NOT Covered</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Damage caused by accidents, scratches, or improper cleaning</li>
                  <li>• Damage from window mechanisms or defrosters</li>
                  <li>• Normal wear and tear or external damage</li>
                  <li>• Removal or reinstallation costs</li>
                  <li>• Tint applied to damaged or compromised glass</li>
                </ul>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Warranty Claims</h3>
                <p className="text-slate-400 text-sm">
                  To make a warranty claim, contact us with your original receipt and photos of the issue. 
                  Warranty work will be carried out at our premises during normal business hours. 
                  We reserve the right to inspect the vehicle before approving any warranty claim.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Job Duration */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">4. Job Duration & Timing</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-blue-400 font-rajdhani font-bold">Important Notice</p>
                <p className="text-slate-300 text-sm mt-1">
                  All job duration times displayed on our website and booking system are <span className="font-bold">approximate estimates for guidance only</span>. 
                  These times should not be taken literally or as guaranteed completion times.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Factors Affecting Duration</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Vehicle size, shape, and window complexity</li>
                  <li>• Condition of existing glass or paintwork</li>
                  <li>• Weather conditions (especially for outdoor work)</li>
                  <li>• Removal of existing tint or coatings</li>
                  <li>• Curing time requirements for certain products</li>
                </ul>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Overnight Jobs</h3>
                <p className="text-slate-400 text-sm">
                  Some services (full vehicle wraps, extensive paint correction) may require your vehicle to remain overnight. 
                  This will be communicated during booking and must be agreed upon before confirmation.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Vehicle Care */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Car className="w-5 h-5 text-purple-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">5. Vehicle Care & Aftercare</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">After Window Tinting</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Do not roll down windows for <span className="text-saturn-orange">48-72 hours</span> after installation</li>
                  <li>• Small water bubbles may appear initially - these will disappear within 2-4 weeks</li>
                  <li>• Wait at least 30 days before cleaning newly tinted windows</li>
                  <li>• Use only ammonia-free cleaning products on tinted windows</li>
                </ul>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">After Ceramic Coating</h3>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Avoid washing your vehicle for <span className="text-saturn-orange">7 days</span> after application</li>
                  <li>• Avoid parking under trees or areas with bird droppings during curing</li>
                  <li>• Use pH-neutral car shampoos for maintenance washing</li>
                </ul>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Vehicle Collection</h3>
                <p className="text-slate-400 text-sm">
                  Vehicles must be collected on the agreed date. Storage fees of €25 per day may apply for vehicles 
                  not collected within 48 hours of job completion notification.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Liability */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">6. Liability & Disclaimers</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Limitation of Liability</h3>
                <p className="text-slate-400 text-sm">
                  SaturnStyling's liability is limited to the cost of the service provided. We are not liable for any 
                  indirect, incidental, or consequential damages arising from our services.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Pre-Existing Conditions</h3>
                <p className="text-slate-400 text-sm">
                  We are not responsible for pre-existing damage, defects, or conditions in your vehicle that may become 
                  more apparent after our services (e.g., scratches revealed after paint correction, glass chips exposed 
                  during tint removal).
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Road Traffic Laws</h3>
                <p className="text-slate-400 text-sm">
                  Under Irish RSA regulations, the front windscreen and front side windows must each allow at least 65% Visible Light Transmission (VLT). Rear side windows and the rear windscreen have no legal restriction.
                  It is the customer's responsibility to ensure their chosen tint complies with legal requirements. 
                  SaturnStyling is not liable for any penalties arising from non-compliant tint choices.
                </p>
              </div>
              <div>
                <h3 className="font-rajdhani font-bold text-white mb-2">Personal Belongings</h3>
                <p className="text-slate-400 text-sm">
                  Please remove all personal belongings from your vehicle before drop-off. SaturnStyling is not responsible 
                  for any items left in vehicles during service.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Business Information */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <h2 className="font-orbitron text-xl font-bold text-white">7. Business Information</h2>
            </div>
            <div className="glass rounded-xl p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-rajdhani font-bold text-white mb-2">Trading Name</h3>
                  <p className="text-slate-400 text-sm">SaturnStyling</p>
                </div>
                <div>
                  <h3 className="font-rajdhani font-bold text-white mb-2">Location</h3>
                  <p className="text-slate-400 text-sm">
                    Unit 4, Platin Industrial Estate<br />
                    Platin, Duleek, Co. Meath<br />
                    A92 E8WK, Ireland
                  </p>
                </div>
                <div>
                  <h3 className="font-rajdhani font-bold text-white mb-2">Contact</h3>
                  <p className="text-slate-400 text-sm">
                    Phone: +353 85 823 7373<br />
                    Email: saturnstyling@gmail.com
                  </p>
                </div>
                <div>
                  <h3 className="font-rajdhani font-bold text-white mb-2">Business Hours</h3>
                  <p className="text-slate-400 text-sm">
                    Mon, Tue, Wed, Fri, Sat: 10:00 AM - 6:00 PM<br />
                    <span className="text-blue-400">Twilight Thursday: 12:00 PM - 8:00 PM</span><br />
                    <span className="text-purple-400">Premium Sunday: 10:00 AM - 2:00 PM (+€50)</span><br />
                    Lunch: 12:30 PM - 1:30 PM (Thu: 2:30 PM - 3:30 PM)
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Governing Law */}
          <section className="mb-10">
            <div className="glass rounded-xl p-6">
              <h2 className="font-orbitron text-xl font-bold text-white mb-4">8. Governing Law</h2>
              <p className="text-slate-400 text-sm">
                These terms and conditions are governed by and construed in accordance with the laws of the Republic of Ireland. 
                Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the Irish courts.
              </p>
              <p className="text-slate-400 text-sm mt-4">
                Your statutory rights under Irish consumer protection law are not affected by these terms.
              </p>
            </div>
          </section>

          {/* Agreement */}
          <div className="glass rounded-xl p-6 border-saturn-orange/30">
            <p className="text-slate-300 text-sm">
              By booking a service with SaturnStyling, you acknowledge that you have read, understood, and agree to these 
              Terms and Conditions. If you have any questions, please contact us before making your booking.
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-8 text-center">
            <Link to="/">
              <Button className="bg-saturn-orange hover:bg-saturn-orange-hover text-white font-rajdhani font-bold">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} SaturnStyling. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
